import { useAuthStore } from '@/stores/useAuthStore'
import { useCubeStore } from '@/stores/useCubeStore'
import Axios, { type InternalAxiosRequestConfig } from 'axios'

function makeClient(applyAuth: (config: InternalAxiosRequestConfig) => void) {
  const client = Axios.create()
  client.interceptors.request.use((config) => {
    applyAuth(config)
    return config
  })
  return client
}

/** Main backend — authenticates via `efToken` header.
 *  @example backendApi.get('/app/getAppConfig') */
const backendApi = makeClient((config) => {
  const auth = useAuthStore()
  config.baseURL = auth.urls.backend
  config.headers.efToken = auth.backendAccessToken
})

const backendV3Api = makeClient((config) => {
  const auth = useAuthStore()
  config.baseURL = import.meta.env.VITE_BACKEND_V3_API_URL
  config.headers.efToken = auth.backendAccessToken
})

/** Reem API — authenticates via `access_token` query param.
 *  @example reemApi.get('/efvertragliste') */
const reemApi = makeClient((config) => {
  const auth = useAuthStore()
  config.baseURL = auth.urls.reem
  config.params = { ...config.params, access_token: auth.backendAccessToken }
})

/** Lukas API — authenticates via `access_token` query param.
 *  @example lukasApi.get('/users/settingslist') */
const lukasApi = makeClient((config) => {
  const auth = useAuthStore()
  config.baseURL = auth.urls.lukas
  config.params = { ...config.params, access_token: auth.backendAccessToken }
})

/** Device manager — authenticates via `accessToken` + `cubeName` headers.
 *  @example deviceManagerApi.get('/devicesimple') */
const deviceManagerApi = makeClient((config) => {
  const auth = useAuthStore()
  const cube = useCubeStore()
  config.baseURL = auth.urls.deviceManager
  config.headers.accessToken = auth.backendAccessToken
  config.headers.cubeName = cube.name
})

/** Strapi CMS — authenticates via `Authorization: Bearer` header.
 *  @example strapiApi.get('/api/maintenance-message') */
const strapiApi = makeClient((config) => {
  const auth = useAuthStore()
  config.baseURL = auth.urls.strapiBaseUrl
  config.headers.Authorization = `Bearer ${auth.strapiToken}`
})

/** Proxy — authenticates via `efToken` header, base URL from env.
 *  @example proxyApi.get('/articles') */
const proxyApi = makeClient((config) => {
  const auth = useAuthStore()
  config.baseURL = import.meta.env.VITE_PROXY_URL
  config.headers.efToken = auth.backendAccessToken
})

/** Balena device — authenticates via `accessToken` header. Always pass an absolute URL.
 *  @example balenaApi.post(`https://${host}/api/watch/WatchFace`, body) */
const balenaApi = makeClient((config) => {
  const auth = useAuthStore()
  config.headers.accessToken = auth.backendAccessToken
})

/** CLP backend — no auth. Base URL from env, falls back to main backend.
 *  @example clpApi.get('/app/CLP/profileName/2024-1-1/2024-1-31') */
const clpApi = makeClient((config) => {
  config.baseURL = import.meta.env.VITE_CLP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL
})

export function useHttpClient() {

  let client;
  function getHttpClient() {
    if (!client) {
      const auth = useAuthStore()
      client = Axios.create({
        baseURL: auth.urls.backend,
        headers: {
          efToken: auth.backendAccessToken,
        }
      })
    }

    return client
  }

  return {
    getHttpClient,
    // apis
    backendApi,
    backendV3Api,
    reemApi,
    lukasApi,
    deviceManagerApi,
    strapiApi,
    proxyApi,
    balenaApi,
    clpApi,
  }
}
